import request from './request';
import makeLogger from './makeLogger';
import { ERROR_TEMPLATES } from 'config/errorCodes';

const log = makeLogger('dynamicModificationRulesService');

const BASE_URL = '/settings/dynamic-modification-rules';

const API_ERROR_MESSAGES = {
  'dynamicModificationRule.unique': 'A rule with this name already exists',
  'dynamicModificationRule.duplicateStageNo': 'Each stage needs its own number',
  'dynamicModificationRule.oneInitialStage': 'Only one stage can be the starting stage',
  'dynamicModificationRule.initialMustInspect':
    'The starting stage has to ask the question, not skip it',
  'dynamicModificationRule.unknownStage': 'Pick a stage that exists in this rule',
  'dynamicModificationRule.skipNeedsEnd':
    'Say after how many inspections or days the skip ends',
  'dynamicModificationRule.countAndStageTogether':
    'Fill in both the number and the stage to go to, or neither',
  'any.required': ERROR_TEMPLATES.required,
  'string.empty': ERROR_TEMPLATES.required,
};

const toMessages = (details = {}) =>
  Object.entries(details).reduce(
    (all, [path, codes]) => ({
      ...all,
      [path]: codes.map((code) => API_ERROR_MESSAGES[code] || code),
    }),
    {},
  );

// The rule list rarely changes during a session; the question forms and detail
// views share one request and reload it after a rule is saved or deleted.
let optionsRequest = null;
let loadedOptions = null;

const getRules = (params = {}) =>
  request.get(BASE_URL, { params: { page: 1, pageSize: 0, ...params } });

const getRuleOptions = () => {
  if (!optionsRequest) {
    optionsRequest = getRules()
      .then((response) => {
        loadedOptions = response.data.map((rule) => ({
          value: Number(rule.id),
          label: rule.name,
        }));
        return loadedOptions;
      })
      .catch((e) => {
        log('Could not load dynamic modification rules', e);
        optionsRequest = null;
        return [];
      });
  }
  return optionsRequest;
};

/** Options already loaded this session, or null. */
const getLoadedRuleOptions = () => loadedOptions;

const forgetRuleOptions = () => {
  optionsRequest = null;
  loadedOptions = null;
};

const saveRule = (rule) => {
  const method = rule.id ? 'put' : 'post';
  const url = rule.id ? `${BASE_URL}/${rule.id}` : BASE_URL;

  return request[method](url, rule)
    .then((response) => {
      forgetRuleOptions();
      return response;
    })
    .catch((e) => {
      const data = e?.response?.data;
      return Promise.reject(
        data?.errorCode === 'entity_body_001'
          ? toMessages(data.details)
          : { form: ['The rule could not be saved. Please try again.'] },
      );
    });
};

const deleteRule = (id) =>
  request.delete(`${BASE_URL}/${id}`).then(() => forgetRuleOptions());

const dynamicModificationRulesService = {
  getRules,
  getRuleOptions,
  getLoadedRuleOptions,
  saveRule,
  deleteRule,
};

export default dynamicModificationRulesService;
